import type { RegionWalkPath } from "@hyperlight/shared";

/** 8.5 → 5 (2026-08-02 "they run REALLY FAST" play report): a hurried jog
 *  still out-paces the ~1.4 m/s schedule walk 3.5×, without the comedy sprint. */
export const NPC_CATCHUP_SPEED = 5;
const JOIN_DISTANCE = 0.15;
const MAX_REPLANS_PER_FRAME = 2;

export interface NpcCatchupRouter {
  walkPath(u0: number, v0: number, u1: number, v1: number): RegionWalkPath;
}

export interface NpcCatchupState {
  path: RegionWalkPath;
  distance: number;
}

export interface NpcCatchupStep {
  state: NpcCatchupState | null;
  u: number;
  v: number;
  heading: number;
}

function pointAt(path: RegionWalkPath, distance: number): { u: number; v: number; heading: number } {
  if (path.pts.length < 4) return { u: path.pts[0] ?? 0, v: path.pts[1] ?? 0, heading: 0 };
  const d = Math.max(0, Math.min(path.len, distance));
  let segment = 1;
  while (segment + 1 < path.cum.length && path.cum[segment] < d) segment++;
  const d0 = path.cum[segment - 1], d1 = path.cum[segment];
  const k = d1 > d0 ? (d - d0) / (d1 - d0) : 0;
  const i = (segment - 1) * 2, j = segment * 2;
  return {
    u: path.pts[i] + (path.pts[j] - path.pts[i]) * k,
    v: path.pts[i + 1] + (path.pts[j + 1] - path.pts[i + 1]) * k,
    heading: Math.atan2(path.pts[j + 1] - path.pts[i + 1], path.pts[j] - path.pts[i]),
  };
}

export function beginNpcCatchup(
  router: NpcCatchupRouter,
  fromU: number,
  fromV: number,
  targetU: number,
  targetV: number,
): NpcCatchupState | null {
  if (Math.hypot(targetU - fromU, targetV - fromV) <= JOIN_DISTANCE) return null;
  return { path: router.walkPath(fromU, fromV, targetU, targetV), distance: 0 };
}

/** Advance along streets toward a schedule target that may itself still be moving. */
export function advanceNpcCatchup(
  router: NpcCatchupRouter,
  initial: NpcCatchupState,
  targetU: number,
  targetV: number,
  dt: number,
): NpcCatchupStep {
  let state = initial;
  // OVER-RUN FIX (2026-08-02 play report: "they overshoot where they would
  // have been"): the path targets a STALE snapshot, and the join test only
  // fired at the path's END — a live target that doubled back along the route
  // was sailed straight past, all the way to the stale endpoint, before the
  // replan turned the runner around. Two rules close it:
  //  1. JOIN against the LIVE target every frame, wherever the runner is.
  //  2. Never travel further this frame than the live gap — arrive, don't fly by.
  const start = pointAt(state.path, state.distance);
  const liveGap = Math.hypot(targetU - start.u, targetV - start.v);
  if (liveGap <= JOIN_DISTANCE) {
    return { state: null, u: targetU, v: targetV, heading: start.heading };
  }
  let budget = Math.min(Math.max(0, dt) * NPC_CATCHUP_SPEED, liveGap);
  for (let attempt = 0; attempt <= MAX_REPLANS_PER_FRAME; attempt++) {
    const remaining = Math.max(0, state.path.len - state.distance);
    const travel = Math.min(remaining, budget);
    state.distance += travel;
    budget -= travel;
    const point = pointAt(state.path, state.distance);
    if (Math.hypot(targetU - point.u, targetV - point.v) <= JOIN_DISTANCE) {
      return { state: null, u: targetU, v: targetV, heading: point.heading };
    }
    if (state.distance + 1e-6 < state.path.len) return { state, ...point };
    state = { path: router.walkPath(point.u, point.v, targetU, targetV), distance: 0 };
    if (budget <= 0) return { state, ...point };
  }
  const point = pointAt(state.path, state.distance);
  return { state, ...point };
}
